import React, { Fragment, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import Box from '@ui/Box'
import { Color } from '@interfaces/designSystem'
import Input from '@ui/Input'
import { AiWizardRequest, AiWizardResponse } from '@interfaces/widgets'
import { getAiWizardResult, publishPreviewWidget, saveWidget } from '@api/widgets'
import OnboardingPopup from '@components/OnboardingPopup/OnboardingPopup'
import { Button, Modal, Result } from 'antd'
import { Spin } from 'antd/lib'
import { BuilderContext } from '../../app/builder/[id]/utils'
import { produce } from 'immer'
import { generateCardId } from '@utils/index'
import { WidgetCardStyleInterface } from '@enhanci/enhanci-lib'
import { getDefaultConfig } from '@api/templates'
import MenuButton from '@ui/MenuButton'
import { FaWandMagicSparkles } from 'react-icons/fa6'
import Flex from '@ui/Flex'
import { AnalyticsEvents, sendAnalyticsEvent } from '@utils/analytics'
import { useNotification } from '@ui/NotificationProvider'
import IconButton from '@ui/IconButton'
import { cloneDeep } from 'lodash'
import { withHandledErrors } from '@utils/errors'

const Btn = styled.div<{ $selected?: boolean }>`
  border: 1px solid ${({ $selected }) => ($selected ? Color.Blue_70 : Color.Grey_10)};
  background-color: ${({ $selected }) => ($selected ? Color.Blue_70 : Color.White)};
  color: ${({ $selected }) => ($selected ? Color.White : Color.Black)};
  border-radius: 8px;
  padding: 8px;
  margin: 8px 0;
  cursor: pointer;
  transition: border 0.3s ease;

  &:hover {
    border: 1px solid ${Color.Blue_70};
  }
`

const industries = [
  {
    name: 'Customer Relationship Management (CRM)',
    text: 'Help manage customer relationships, track sales, and streamline communication.',
  },
  {
    name: 'Project Management',
    text: 'Assist in planning, organizing, and managing projects, enhancing collaboration.',
  },
  {
    name: 'Human Resources (HR)',
    text: 'Manage employee data, recruitment, onboarding, attendance, performance, and training.',
  },
  {
    name: 'Accounting and Financial Management',
    text: 'Automate invoicing, expense tracking, reporting, and tax management.',
  },
  {
    name: 'Marketing Automation',
    text: 'Optimizes campaigns including email, lead generation, social media, and analytics.',
  },
  {
    name: 'Customer Support and Help Desk',
    text: 'Manage inquiries, troubleshoot issues, and provide timely assistance.',
  },
  {
    name: 'E-commerce',
    text: 'Enables online store management, payments, inventory tracking, and seamless shopping.',
  },
  {
    name: 'Collaboration and Communication',
    text: 'Facilitate file sharing, messaging, task management, and virtual meetings.',
  },
  {
    name: 'Data Analytics and Business Intelligence',
    text: 'Provide insights for decision-making and performance monitoring.',
  },
  {
    name: 'Learning Management System (LMS)',
    text: 'Creates, delivers, and tracks online courses and training programs.',
  },
]

const customerCount = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  '10001+',
]

type ActionType = 'wizard' | 'result' | 'onboarding' | undefined

type Props = {
  noTrigger?: boolean
  initialAction?: ActionType
}

const AiWizard = ({ noTrigger, initialAction }: Props) => {
  const { widget, setWidget } = useContext(BuilderContext)
  const [action, setAction] = useState<ActionType>(initialAction)
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error' | 'excess_limit'
  >('idle')
  const [state, setState] = useState<AiWizardRequest>({})
  const [otherIndustry, setOtherIndustry] = useState(false)
  const [result, setResult] = useState<AiWizardResponse[]>()
  const notification = useNotification()

  useEffect(() => {
    setAction(initialAction)
  }, [initialAction])

  const onGenerate = async () => {
    setAction('result')
    setStatus('loading')

    try {
      const resp = await getAiWizardResult({ ...state, widgetId: widget.id })
      if (!resp.data || !resp.data.length) {
        throw new Error('No data')
      }
      setResult(resp.data)
      setStatus('success')
      sendAnalyticsEvent(AnalyticsEvents.aiWizardSuccess)
    } catch (e) {
      setStatus(e?.response?.status === 429 ? 'excess_limit' : 'error')
      sendAnalyticsEvent(
        e?.response?.status === 429
          ? AnalyticsEvents.aiWizardExcessLimit
          : AnalyticsEvents.aiWizardFailed,
      )
    }
  }

  const onSetCards = async () => {
    if (!result) {
      return
    }

    const config = await getDefaultConfig(widget.template!.id)

    const fallbackCards = cloneDeep(widget.config.cards)

    const newWidget = produce(widget, draft => {
      draft.config.cards = result.map(item => ({
        id: generateCardId(),
        title: item.title,
        monthly_price: item.price,
        annual_price: item.price,
        buttonLabel: item.cta_button_title,
        description: item.description,
        currency: {
          code: 'USD',
          sign: '$',
        },
        state: item.highlighted ? 'highlighted' : 'default',
        style: config.cards[0]?.style || ({} as WidgetCardStyleInterface),
        buttonLink: '',
        features: item.features.map(feature => ({
          text: feature.text,
          style: 'check',
        })),
      }))
    })

    setWidget(newWidget)

    notification.showSuccess(
      'Your pricing plans are ready 🚀',
      <>
        Do you like it?{' '}
        <Box display="inline-flex">
          <Box mr={16}>
            <IconButton
              type="primary"
              size="small"
              onClick={async () => {
                await withHandledErrors(
                  notification,
                  async () => {
                    const resp = await saveWidget(newWidget)
                    saveWidget(resp)

                    // do now await here, because we don't want to wait for the response
                    publishPreviewWidget(widget.id!)
                  },
                  'Saved successfully',
                )
                sendAnalyticsEvent(AnalyticsEvents.aiWizardKeep)
              }}
            >
              Yes, keep it
            </IconButton>
          </Box>
          <IconButton
            size="small"
            type="default"
            onClick={() => {
              setWidget(
                produce(widget, draft => {
                  draft.config.cards = fallbackCards
                }),
              )
              sendAnalyticsEvent(AnalyticsEvents.aiWizardRollback)
            }}
          >
            No, rollback changes
          </IconButton>
        </Box>
      </>,
      { duration: 0 },
    )

    setAction(undefined)
  }

  return (
    <>
      {!noTrigger && (
        <MenuButton
          icon={FaWandMagicSparkles}
          onClick={() => {
            setAction('onboarding')
            sendAnalyticsEvent(AnalyticsEvents.aiWizardClicked)
          }}
          style={{
            backgroundColor: Color.Pink_20,
          }}
        >
          Try AI wizard
        </MenuButton>
      )}

      <OnboardingPopup
        open={action === 'wizard'}
        onClose={() => setAction(undefined)}
        title="Generate pricing plans with AI Wizard 🪄"
        preload={[]}
        onSubmit={onGenerate}
        submitBtnLabel="Generate my pricing plans 🚀"
        nextDisabled={step => {
          if (step === 0) {
            return !state.category
          }

          if (step === 1) {
            return !state.customersCount
          }

          if (step === 2) {
            return !state.employeesCount
          }

          return false
        }}
        steps={[
          <Fragment key={1}>
            <Box fontSize={18}>Choose your industry</Box>
            <Box>
              {industries.map(item => (
                <Btn
                  key={item.name}
                  $selected={state.category === item.name}
                  onClick={() => {
                    setOtherIndustry(false)
                    setState(prevState => ({
                      ...prevState,
                      category: item.name,
                    }))
                  }}
                >
                  <Box>{item.name}</Box>
                  <Box
                    fontSize={14}
                    color={state.category === item.name ? Color.Grey_10 : Color.Grey_80}
                  >
                    {item.text}
                  </Box>
                </Btn>
              ))}
              <Btn
                $selected={otherIndustry}
                onClick={() => {
                  setOtherIndustry(true)
                  setState(prevState => ({
                    ...prevState,
                    category: undefined,
                  }))
                }}
              >
                <Box>Other</Box>
              </Btn>
              {otherIndustry && (
                <Box mt={8}>
                  <Input
                    maxLength={80}
                    label="Please specify your industry in several words"
                    size="large"
                    value={state.category}
                    onChange={e =>
                      setState(prevState => ({
                        ...prevState,
                        category: e.target.value,
                      }))
                    }
                  />
                </Box>
              )}
            </Box>
          </Fragment>,
          <Fragment key={2}>
            <Box fontSize={18}>Tell us how many customers you have</Box>
            <Box>
              {customerCount.map(item => (
                <Btn
                  key={item}
                  $selected={state.customersCount === item}
                  onClick={() => {
                    setState(prevState => ({
                      ...prevState,
                      customersCount: item,
                    }))
                  }}
                >
                  <Box>{item}</Box>
                </Btn>
              ))}
            </Box>
          </Fragment>,
          <Fragment key={3}>
            <Box fontSize={18}>Tell us how many employees you have</Box>
            <Box>
              {customerCount.map(item => (
                <Btn
                  key={item}
                  $selected={state.employeesCount === item}
                  onClick={() => {
                    setState(prevState => ({
                      ...prevState,
                      employeesCount: item,
                    }))
                  }}
                >
                  <Box>{item}</Box>
                </Btn>
              ))}
            </Box>
          </Fragment>,
          <Fragment key={4}>
            <Box mb={8} fontSize={18}>
              If you have your website, please specify URL
            </Box>
            <Box>
              <Input
                value={state.url}
                size="large"
                onChange={e =>
                  setState(prevState => ({
                    ...prevState,
                    url: e.target.value,
                  }))
                }
              />
            </Box>
          </Fragment>,
          <Fragment key={5}>
            <Box mb={8} fontSize={18}>
              If you have a competitor, tell us it's website URL
            </Box>
            <Box>
              <Input
                size="large"
                value={state.competitorUrl}
                onChange={e =>
                  setState(prevState => ({
                    ...prevState,
                    competitorUrl: e.target.value,
                  }))
                }
              />
            </Box>
          </Fragment>,
        ]}
      />
      <Modal
        open={action === 'result'}
        onCancel={() => setAction(undefined)}
        footer={null}
      >
        {status === 'loading' && (
          <Box p={106}>
            <Spin tip="We are generating your pricing plans..." size="large">
              <Box />
            </Spin>
          </Box>
        )}
        {(status === 'error' || status === 'success' || status === 'excess_limit') && (
          <Result
            status={status === 'success' ? 'success' : 'error'}
            title={
              status === 'success'
                ? 'Your pricing plans are ready 🚀'
                : status === 'excess_limit'
                ? 'You have reached the limit of requests for today 😔'
                : 'We could not generate your pricing plans 😔'
            }
            subTitle={
              status === 'excess_limit'
                ? 'Please try again tomorrow.'
                : status === 'error'
                ? 'Try to change your input and try again.'
                : undefined
            }
            extra={
              status === 'success'
                ? [
                    <Button key="checkitout" onClick={onSetCards}>
                      Check it out!
                    </Button>,
                  ]
                : [
                    <Button key="checkitout" onClick={() => setAction('wizard')}>
                      Try again
                    </Button>,
                  ]
            }
          />
        )}
      </Modal>
      <Modal
        open={action === 'onboarding'}
        onCancel={() => setAction(undefined)}
        footer={null}
      >
        <Box p="30px 30px">
          <Box fontSize={26}>Try our AI Wizard! 🪄</Box>
          <Box mt={26} fontSize={18}>
            Creating pricing plans just got easier! <br /> Our AI wizard is here to help
            you tailor your pricing plans based on your industry, customers, and
            competitors.
          </Box>
          <Flex mt={16} justifyContent="center">
            <img
              src="/img/ai_wizard.png"
              alt="AI Wizard"
              style={{ width: '100%', maxWidth: '265px', height: 'auto' }}
            />
          </Flex>
          <Flex justifyContent="center" mt={26}>
            <Button
              size="large"
              onClick={() => {
                setAction('wizard')
                sendAnalyticsEvent(AnalyticsEvents.aiWizardStart)
              }}
              type="primary"
            >
              Try now
            </Button>
          </Flex>
        </Box>
      </Modal>
    </>
  )
}

export default AiWizard
